/* istanbul ignore file */
import './i18n';

import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Switch, Redirect } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Modal as CustomModal } from './components/Modal';
import ReactTooltip from 'react-tooltip';

import { PageSpinner, ToastCloseButton } from '@td-client-frontend/common';
import Layout from './components/Layout/BasicLayout';

// Public
import Login from './containers/Login';
import ForgotPassword from './containers/ForgotPassword';
import ChangePassword from './containers/ChangePassword';

// Auth utils
import { AuthProvider } from './context/AuthContext';
import { AxiosProvider } from './context/AxiosContext';
import IdleTimer from './context/hoc/IdleTimer';
import { NoPermissions } from './containers/NoPermissions';
import { AuthorizationRedirect } from './utils/authUtils/AuthorizationRedirect';
import { AdminRoute } from './utils/authUtils/AdminRoute';
import { AuthenticatedRoute } from './utils/authUtils/AuthenticatedRoute';
import { PermMode } from './components/WithPermission';
import Jobs from './flows/Reseller/Jobs';
import JobApproval from './flows/Reseller/JobApproval';
import TemplateBuilder from './flows/Reseller/TemplateBuilder';
import { EngagementCenter, MarketingProfileWithContext as MarketingProfiles } from './components/Marketing';

import ManageUsers from './flows/ManageUsers';
import ManageRoles from './flows/ManageRoles';
import EmployerGroups from './flows/ManageGroups';
import UpdatePassword from './containers/UpdatePassword';
import ActivityLog from './flows/Activities/ActivityLogs';
import SystemActivityLog from './flows/SystemActivities/SystemActivityLog';
import ManageReports from './flows/ManageReports';
import ManageMembers from './flows/Members/MembersList';
import { MarketingPreferencesWithPendingChanges } from './components/Marketing/MarketingPreferences/MarketingPreferences';
import Dashboard from './flows/Dashboard';
import { MarketingProfileDetails } from './components/Marketing/MarketingProfileDetails';
import WelcomeEmailInstruction from './flows/WelcomeEmailInstruction';
import { LoginBannerModal } from './components/LoginBannerModal/LoginBannerModal';
import { LoginBannerProvider } from './context/BannerContext';
import { ResetPasswordContainer } from './containers/ResetPasswordContainer';
import { Permissions } from './@types/Permissions';
import { FeatureToggleProvider } from './context/FeatureToggleContext';

// Lazy loaded Auth pages
const Admin = lazy(() => import('./containers/Admin'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false
    }
  }
});

const LoadingFallback = () => (
  <Layout>
    <PageSpinner />
  </Layout>
);

const UnauthenticatedRoutes = () => (
  <Switch>
    <Layout>
      <Route path="/forgot-password">
        <ForgotPassword />
      </Route>
    </Layout>
  </Switch>
);

const AppRoutes = () => {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Switch>
        <Redirect path="/" exact to="/checking-permissions" />
        <AuthenticatedRoute unauthenticated exact path="/login">
          <Login />
        </AuthenticatedRoute>
        <AuthenticatedRoute exact path="/welcome-email-instruction">
          <WelcomeEmailInstruction />
        </AuthenticatedRoute>
        <AuthenticatedRoute unauthenticated exact path="/password_token/:token">
          <ResetPasswordContainer />
        </AuthenticatedRoute>
        <AuthenticatedRoute exact path="/checking-permissions">
          <AuthorizationRedirect />
        </AuthenticatedRoute>
        <AuthenticatedRoute exact path="/home">
          <Dashboard />
        </AuthenticatedRoute>
        <AuthenticatedRoute
          path="/groups"
          exact
          requiredPerms={[Permissions.GroupRead, Permissions.DraftRead]}
          permsMode={PermMode.ANY}
        >
          <EmployerGroups />
        </AuthenticatedRoute>
        <AuthenticatedRoute
          exact
          path="/roles"
          requiredPerms={[Permissions.RoleRead, Permissions.RoleWrite, Permissions.RoleCreate]}
          permsMode={PermMode.ANY}
        >
          <ManageRoles />
        </AuthenticatedRoute>
        <AuthenticatedRoute exact path="/members">
          <ManageMembers />
        </AuthenticatedRoute>
        <AuthenticatedRoute
          path="/users"
          requiredPerms={[Permissions.UserRead, Permissions.UserWrite, Permissions.UserCreate]}
          permsMode={PermMode.ANY}
        >
          <ManageUsers />
        </AuthenticatedRoute>
        <AuthenticatedRoute
          path="/system-activity-log"
          exact
          requiredPerms={['SystemActivityLogRead']}
          permsMode={PermMode.ANY}
        >
          <SystemActivityLog />
        </AuthenticatedRoute>
        <AuthenticatedRoute
          path="/system-activity-log/:id"
          requiredPerms={['SystemActivityLogRead']}
          permsMode={PermMode.ANY}
        >
          <SystemActivityLog />
        </AuthenticatedRoute>
        <AuthenticatedRoute
          path="/reports"
          requiredPerms={[Permissions.ReportEligibilityRead]}
          permsMode={PermMode.ANY}
        >
          <ManageReports />
        </AuthenticatedRoute>
        <AuthenticatedRoute path="/change-password">
          <ChangePassword />
        </AuthenticatedRoute>
        <AuthenticatedRoute path="/update-password">
          <UpdatePassword />
        </AuthenticatedRoute>
        <AuthenticatedRoute path="/no-permissions">
          <NoPermissions />
        </AuthenticatedRoute>
        <AuthenticatedRoute path="/reseller/jobs" requiredPerms={[Permissions.ResellerRead]} exact>
          <Jobs />
        </AuthenticatedRoute>
        <AuthenticatedRoute path="/reseller/job-approval/:id">
          <JobApproval />
        </AuthenticatedRoute>
        <AuthenticatedRoute path="/reseller/templates" requiredPerms={[Permissions.ResellerRead]}>
          <TemplateBuilder />
        </AuthenticatedRoute>
        <AuthenticatedRoute path="/activity-log" exact>
          <ActivityLog />
        </AuthenticatedRoute>
        <AuthenticatedRoute path="/activity-log/:id">
          <ActivityLog />
        </AuthenticatedRoute>
        <AuthenticatedRoute path="/engagement-center" requiredPerms={[Permissions.EngagementCenterRead]}>
          <EngagementCenter />
        </AuthenticatedRoute>
        <AuthenticatedRoute path="/marketing-profiles" requiredPerms={[Permissions.MarketingProfileListRead]}>
          <MarketingProfiles />
        </AuthenticatedRoute>
        <AuthenticatedRoute path="/marketing-profile/:id" requiredPerms={[Permissions.MarketingProfileListRead]}>
          <MarketingProfileDetails />
        </AuthenticatedRoute>
        <AuthenticatedRoute
          path="/marketing-preferences"
          requiredPerms={[
            Permissions.MarketingProfileRetentionRead,
            Permissions.MarketingProfileRegistrationRead,
            Permissions.MarketingProfilePreferencesRead
          ]}
          permsMode={PermMode.ANY}
        >
          <MarketingPreferencesWithPendingChanges />
        </AuthenticatedRoute>
        <AdminRoute path="/admin">
          <Admin />
        </AdminRoute>
        <UnauthenticatedRoutes />
      </Switch>
      <IdleTimer />
    </Suspense>
  );
};

function App() {
  return (
    <Router>
      <QueryClientProvider client={queryClient}>
        <AxiosProvider>
          <FeatureToggleProvider>
            <LoginBannerProvider>
              <CustomModal>
                <AuthProvider>
                  <LoginBannerModal />
                  <AppRoutes />
                  <ToastContainer closeButton={ToastCloseButton} />
                  <ReactTooltip />
                </AuthProvider>
              </CustomModal>
            </LoginBannerProvider>
          </FeatureToggleProvider>
        </AxiosProvider>
      </QueryClientProvider>
    </Router>
  );
}

export default App;
